import React, { useState, ChangeEvent, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface FileUploadFieldProps {
  onChange: (files: FileList | null) => void;
  value?: FileList;
  label: string;
  description?: string;
  stepNumber?: number;
  error?: string;
  required?: boolean;
  accept?: string;
  disabled?: boolean;
  name: string;
  /** Nome do arquivo já salvo anteriormente (storage), para o campo "lembrar" que já foi preenchido ao reabrir o formulário */
  existingFileName?: string;
  /** Oculta o título interno do campo, para uso em contextos que já exibem o título externamente (ex.: FormLabel) */
  hideLabel?: boolean;
}

const MAX_FILE_SIZE = 5000000; // 5MB
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

export const FileUploadField = forwardRef<HTMLInputElement, FileUploadFieldProps>(
  ({ onChange, value, label, description, stepNumber, error, required = false, accept, disabled, name, existingFileName, hideLabel = false, ...props }, ref) => {
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>("");
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const fileName = value && value.length > 0 ? value[0].name : "";
    // Um arquivo pode já ter sido enviado em uma sessão anterior (existingFileName) mesmo sem
    // haver um novo arquivo selecionado agora (fileName) — em ambos os casos o campo deve
    // sinalizar visualmente que já está preenchido.
    const isFilled = Boolean(fileName || existingFileName);
    const displayName = fileName || existingFileName || "";

    // Função para truncar nome do arquivo se for muito longo
    const truncateFileName = (name: string, maxLength: number = 30) => {
      if (name.length <= maxLength) return name;
      
      const extension = name.split('.').pop();
      const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
      const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);
      
      return `${truncatedName}...${extension}`;
    };

    const validateFile = (file: File): string | null => {
      if (file.size > MAX_FILE_SIZE) {
        return "O arquivo é muito grande. Tamanho máximo: 5MB";
      }
      
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        return "Formato de arquivo não suportado. Formatos aceitos: JPG, JPEG, PNG e PDF";
      }
      
      return null;
    };

    const handleDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      setUploadError("");
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const validationError = validateFile(file);
        
        if (validationError) {
          setUploadError(validationError);
          return;
        }
        
        // Criar nova FileList com o arquivo validado
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        const fileList = dataTransfer.files;
        
        onChange(fileList);
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      
      if (files && files.length > 0) {
        // Verificar tamanho do arquivo
        const validationError = validateFile(files[0]);
        if (validationError) {
          toast.error("Erro ao enviar arquivo", {
            description: validationError
          });
          e.target.value = "";
          return;
        }
        
        onChange(files);
      } else {
        onChange(null);
      }
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      setUploadError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      // Criar FileList vazio
      onChange(null);
    };

    return (
      <div className={`border ${error || uploadError ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40" : dragActive ? "border-blue-400 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/40" : isFilled ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40" : "border-gray-200 dark:border-zinc-700"} rounded-md p-3 hover:border-primary transition-colors`}>
        {!hideLabel && (
        <div className="flex items-center">
          {stepNumber && (
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isFilled ? "bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400" : "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"} mr-3`}>
              {isFilled ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                stepNumber
              )}
            </span>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium flex items-center">
              <span className="break-words">{label}</span>
              {required && <span className="text-red-500 ml-1 shrink-0">*</span>}
            </div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1 mb-1">{description}</p>
            )}
          </div>
        </div>
        )}

        <div className={`mt-2 ${stepNumber ? "ml-11" : ""}`}>
          <div
            className={`flex items-center justify-center w-full`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <label
              className={`
                relative flex flex-col items-center justify-center w-full h-28
                border-2 border-dashed rounded-lg cursor-pointer
                ${
                  isFilled
                    ? "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-700"
                    : dragActive
                    ? "bg-blue-50 border-blue-300 dark:bg-blue-950/30 dark:border-blue-700"
                    : error || uploadError
                    ? "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-700"
                    : "bg-gray-50 border-gray-300 hover:bg-gray-100 dark:bg-zinc-800/50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                }
                transition-colors
              `}
            >
              <div className="flex flex-col items-center justify-center px-2 pt-4 pb-4">
                {isFilled ? (
                  <>
                    <div className="flex items-center">
                      <svg
                        className="w-6 h-6 mb-2 text-green-500 dark:text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="mb-1 text-xs text-green-600 dark:text-green-400 text-center max-w-full">
                      <span className="font-semibold break-words" title={displayName}>
                        {truncateFileName(displayName)}
                      </span>
                    </p>
                    <p className="text-xs text-green-500 dark:text-green-400">
                      {fileName ? "Clique para trocar o arquivo" : "Documento já enviado — clique para substituir"}
                    </p>
                    {fileName && (
                      <button
                        type="button"
                        className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900"
                        onClick={handleRemove}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <svg
                      className={`w-6 h-6 mb-2 ${error || uploadError ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                      <span className="font-semibold">Clique para enviar</span> ou arraste o arquivo
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      JPG, JPEG, PNG ou PDF (Max. 5MB)
                    </p>
                  </>
                )}
              </div>
              <Input
                ref={ref}
                type="file"
                accept={accept}
                onChange={handleChange}
                disabled={disabled}
                name={name}
                className="hidden"
                {...props}
              />
            </label>
          </div>
          {(error || uploadError) && (
            <div className="flex items-start mt-2 text-red-600 dark:text-red-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm">{uploadError || error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

FileUploadField.displayName = "FileUploadField"; 